import React, { useEffect, useRef, useState } from 'react';
import { Box, useMediaQuery, useTheme } from '@material-ui/core';
import { useSnackbar } from 'notistack';
import valid from 'card-validator';
import { useAppContext } from 'contexts/AppContext';
import defaultLocales from 'globals/locales/default';
import {
  formatCcNumber,
  getFutureYearOptions,
  getMonthOptions,
  preventLosingCaretPosition,
  removeSpaces,
  wait,
} from 'globals/utils';
import FsSimpleInput from 'ui-elements/form-elements/FsDefaultInput';
import FsPendingButton from 'ui-elements/buttons/FsPendingButton';
import FsButton from 'ui-elements/buttons/FsButton';
import FsDefaultSelect from 'ui-elements/form-elements/FsDefaultSelect';
import { FsFormGridWrapper } from 'ui-elements/form-elements/FsFormGridWrapper/FsFormGridWrapper';
import { FsFormFullWidthContainer } from 'ui-elements/form-elements/FsFormFullWidthContainer/FsFormFullWidthContainer';
import { FsFormGridItem } from 'ui-elements/form-elements/FsFormGridItem/FsFormGridItem';
import FsCreditCardInput from 'ui-elements/form-elements/FsCreditCardInput';
import FsErrorLabel from 'ui-elements/form-elements/FsErrorLabel/FsErrorLabel';

export interface PaymentMethodFormProps {
  ccName: string;
  ccNumber: string;
  ccExpMonth: string;
  ccExpYear: string;
  ccCsc: string;
  ccBrand: string;
}

interface FsPaymentMethodFormProps extends Partial<PaymentMethodFormProps> {
  onClose?: () => void;
  onChange?: (isValid: boolean, formData?: PaymentMethodFormProps) => void;
  showButtons?: boolean;
}

interface ShowErrorsProps {
  [key: string]: boolean;
}

const FsPaymentMethodForm: React.FC<FsPaymentMethodFormProps> = ({
  onClose: handleClose,
  onChange: handleChange,
  showButtons = true,
  ccName: defaultCcName = '',
  ccNumber: defaultCcNumber = '',
  ccExpMonth: defaultCcExpMonth = '',
  ccExpYear: defaultCcExpYear = '',
  ccCsc: defaultCcCsc = '',
  ccBrand: defaultCcBrand = '',
}) => {
  const {
    setIsPaymentMethodIssue,
    paymentMethod: { update: updateStore },
  } = useAppContext();
  const formEl = useRef<HTMLFormElement>(null);
  const { enqueueSnackbar } = useSnackbar();
  const [ccName, setCcName] = useState<string>(defaultCcName);
  const [ccNumber, setCcNumber] = useState<string>(
    formatCcNumber(defaultCcNumber)
  );
  const [ccExpMonth, setCcExpMonth] = useState<string>(defaultCcExpMonth);
  const [ccExpYear, setCcExpYear] = useState<string>(defaultCcExpYear);
  const [ccCsc, setCcCsc] = useState<string>(defaultCcCsc);
  const [ccCscMaxLength, setCcCscMaxLength] = useState<number>(3);
  const [pending, setPending] = useState<boolean>(false);
  const [isFormValid, setIsFormValid] = useState<boolean>(false);
  const [showErrors, setShowErrors] = useState<ShowErrorsProps>({});
  const [isFieldValid, setIsFieldValid] = useState<ShowErrorsProps>({});
  const [ccBrand, setCcBrand] = useState<string>(defaultCcBrand);
  const monthOptions = getMonthOptions();
  const yearOptions = getFutureYearOptions();
  const theme = useTheme();
  const isUpSm = useMediaQuery(theme.breakpoints.up('sm'), {
    noSsr: true,
  });

  const fields = [
    {
      name: 'cc-name',
      value: ccName,
      label: defaultLocales.cardHolderName,
      onChange: (e: React.ChangeEvent<HTMLInputElement>): void => {
        setCcName(e.target?.value);
      },
      required: true,
    },
    {
      name: 'cc-number',
      value: ccNumber,
      label: defaultLocales.validCardNumber,
      onChange: (e: React.ChangeEvent<HTMLInputElement>): void => {
        const { isPotentiallyValid, card } = valid.number(e.target.value);
        if (isPotentiallyValid) {
          setCcBrand(card?.type || '');
          setCcCscMaxLength(card?.code.size || 3);
        } else {
          setCcBrand('');
          setCcCscMaxLength(3);
        }
        preventLosingCaretPosition(e);
        setCcNumber(formatCcNumber(e.target?.value));
      },
      required: true,
      type: 'tel',
      InputProps: {
        inputProps: {
          maxLength: '19',
        },
      },
    },
    {
      isSelect: true,
      options: monthOptions,
      name: 'cc-exp-month',
      value: ccExpMonth,
      label: defaultLocales.expiryMonth,
      onChange: (e: React.ChangeEvent<HTMLInputElement>): void => {
        setCcExpMonth(`${e.target?.value}`);
      },
      required: true,
    },
    {
      isSelect: true,
      options: yearOptions,
      name: 'cc-exp-year',
      value: ccExpYear,
      label: defaultLocales.expiryYear,
      onChange: (e: React.ChangeEvent<HTMLInputElement>): void => {
        setCcExpYear(`${e.target?.value}`);
      },
      required: true,
    },
    {
      name: 'cc-csc',
      value: ccCsc,
      label: defaultLocales.cvvCsc,
      onChange: (e: React.ChangeEvent<HTMLInputElement>): void => {
        if (e.target.value.length > e.target.maxLength) {
          setCcCsc(e.target.value.slice(0, e.target.maxLength));
          return;
        }
        setCcCsc(e.target.value);
      },
      type: 'number',
      InputProps: {
        inputProps: {
          maxLength: ccCscMaxLength,
        },
      },
      required: true,
    },
  ];

  const [ccNameField, ccNumberField, ...otherFields] = fields;

  const handleShowError = (key: string) => {
    setShowErrors((currentState) => ({
      ...currentState,
      [key]: true,
    }));
  };

  const handleHideError = (key: string) => {
    setShowErrors((currentState) => ({
      ...currentState,
      [key]: false,
    }));
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setPending(true);
    await wait(1000);
    setPending(false);
    await updateStore();
    setIsPaymentMethodIssue(false);
    enqueueSnackbar(defaultLocales.creditCardChangeNotificationSuccess, {
      variant: 'success',
    });
    if (handleClose) handleClose();
  };

  useEffect(() => {
    const isDefaultFormValid = !!formEl?.current?.checkValidity();
    const { isValid: isCcNameValid } = valid.cardholderName(ccName);
    const { isValid: isCcNumberValid } = valid.number(ccNumber);
    const { isValid: isExpirationMonthValid } = valid.expirationMonth(
      ccExpMonth
    );
    const { isValid: isExpirationYearValid } = valid.expirationYear(
      `${ccExpYear}`,
      5
    );
    const { isValid: isCscValid } = valid.cvv(ccCsc, ccCscMaxLength);

    setIsFieldValid((currentState) => ({
      ...currentState,
      'cc-number': isCcNumberValid,
      'cc-name': isCcNameValid,
      'cc-csc': isCscValid,
    }));

    const isValid =
      isDefaultFormValid &&
      isCcNameValid &&
      isCcNumberValid &&
      isExpirationMonthValid &&
      isExpirationYearValid &&
      isCscValid;
    setIsFormValid(isValid);

    if (handleChange)
      handleChange(isValid, {
        ccName,
        ccNumber: removeSpaces(ccNumber),
        ccExpMonth,
        ccExpYear,
        ccCsc,
        ccBrand,
      });
  }, [
    ccCsc,
    ccExpYear,
    ccExpMonth,
    ccName,
    ccNumber,
    ccCscMaxLength,
    ccBrand,
    handleChange,
  ]);

  return (
    <Box>
      <form ref={formEl} onSubmit={handleSubmit}>
        {[ccNameField, ccNumberField].map(({ name, ...otherInputProps }) => (
          <FsFormFullWidthContainer key={name}>
            {name === 'cc-number' ? (
              <>
                <FsCreditCardInput
                  {...{ ...otherInputProps, name }}
                  error={showErrors[name] && !isFieldValid[name]}
                  ccType={ccBrand}
                  fsVariant="outlined"
                  fullWidth
                  onBlur={() => handleShowError(name)}
                  onFocus={() => handleHideError(name)}
                />
                {showErrors[name] && !isFieldValid[name] && (
                  <FsErrorLabel>
                    {defaultLocales.inputErrors[name]}
                  </FsErrorLabel>
                )}
              </>
            ) : (
              <>
                <FsSimpleInput
                  {...{ ...otherInputProps, name }}
                  error={showErrors[name] && !isFieldValid[name]}
                  fsVariant="outlined"
                  fullWidth
                  onBlur={() => handleShowError(name)}
                  onFocus={() => handleHideError(name)}
                />
                {showErrors[name] && !isFieldValid[name] && (
                  <FsErrorLabel>
                    {defaultLocales.inputErrors['cc-name']}
                  </FsErrorLabel>
                )}
              </>
            )}
          </FsFormFullWidthContainer>
        ))}
        <FsFormGridWrapper isBlock={!isUpSm}>
          {otherFields.map(
            ({ name, isSelect, options, ...otherInputProps }) => (
              <FsFormGridItem key={name}>
                {isSelect && options ? (
                  <FsDefaultSelect
                    {...{ ...otherInputProps, name, options }}
                    fsVariant="outlined"
                    optionCase="sc"
                    fullWidth
                    onBlur={() => handleShowError(name)}
                    onFocus={() => handleHideError(name)}
                  />
                ) : (
                  <Box width="100%">
                    <FsSimpleInput
                      {...{ ...otherInputProps, name }}
                      error={showErrors[name] && !isFieldValid[name]}
                      fsVariant="outlined"
                      fullWidth
                      onBlur={() => handleShowError(name)}
                      onFocus={() => handleHideError(name)}
                    />
                    {showErrors[name] && !isFieldValid[name] && (
                      <FsErrorLabel>
                        {defaultLocales.inputErrors['cc-csc']}
                      </FsErrorLabel>
                    )}
                  </Box>
                )}
              </FsFormGridItem>
            )
          )}
        </FsFormGridWrapper>
        {showButtons && (
          <Box display="flex" flexDirection="row-reverse">
            <FsPendingButton
              type="submit"
              variant="contained"
              color="primary"
              pending={pending}
              width={160}
              disabled={!isFormValid}
            >
              {defaultLocales.change}
            </FsPendingButton>
            <FsButton
              color="inherit"
              onClick={handleClose}
              style={{ marginLeft: 10 }}
            >
              {defaultLocales.close}
            </FsButton>
          </Box>
        )}
      </form>
    </Box>
  );
};

export default FsPaymentMethodForm;
